import React, { Fragment, useState } from 'react';
import { isEmpty } from 'lodash';
import { COLORS } from 'Components/utils/color/colors';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { getLinkProps, getLinkTagName } from 'Components/utils/markup/markup';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { classnames } from 'Utils/classnames/classnames.utils';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { TeaserCard } from 'Components/frameworks/teaser-card/TeaserCard';
import { Title } from 'Components/frameworks/title/Title';
export type ProductFeatureCardProps = {
  banner?: {
    color?: Colors;
    iconName?: IconName;
    label: string;
  };
  className?: string;
  counters?: Array<{
    color?: Colors;
    count: number;
    icon?: IconName;
    label: string;
  }>;
  imageSrc: string;
  isHidden?: boolean;
  key?: string;
  link?: {
    onClick?: () => void;
    target?: string;
    url?: string;
  };
  seeMoreLabel?: string;
  title: string;
  titleColor?: Colors;
  trackingData?: TrackingData;
};
export const ProductFeatureCard = ({
  banner = {},
  className,
  counters = [],
  imageSrc,
  isHidden,
  link,
  seeMoreLabel,
  title,
  titleColor = COLORS.BLACK,
  trackingData,
}: ProductFeatureCardProps) => {
  const [isHovering, setHovering] = useState(false);
  const { isDesktop } = useResponsive();
  const track = useTracking();
  const media = [
    {
      src: imageSrc,
    },
  ];
  const TagName = getLinkTagName(link);
  const linkProps = getLinkProps(link);
  return (
    <TagName
      {...linkProps}
      aria-hidden={isHidden}
      className={classnames(
        'tap-highlight-color-transparent text-initial relative block h-full w-full',
        className,
      )}
      onClick={() => {
        linkProps?.onClick?.();
        trackingData && track(trackingData);
      }}
      onMouseEnter={isDesktop ? () => setHovering(true) : null}
      onMouseLeave={isDesktop ? () => setHovering(false) : null}
      tabIndex={isHidden ? -1 : 0}
    >
      {!!banner.label && (
        <span
          className={classnames(
            `bg-${banner.color || COLORS.SIENNA} transition-transform absolute z-1 flex w-full items-center justify-center gap-10 rounded-t-12 p-10 text-center font-brand text-14 text-white duration-250 ease-in sm:text-12 lg:text-14`,
            {
              'scale-095 transform': isHovering,
            },
          )}
        >
          <Icon name={banner.iconName} width="0.625rem" />
          {banner.label}
        </span>
      )}
      <TeaserCard isActive={isHovering} media={media}>
        <Title className={`my-15 text-${titleColor}`} hasEllipsis theme="title-brand-n3">
          {title}
        </Title>
        {!isEmpty(counters) && (
          <ul className="my-15 h-40 text-14 font-bold">
            {counters.map(({ color = COLORS.BLACK, count, icon, label }) =>
              count ? (
                <li
                  className={`my-5 flex justify-between overflow-hidden font-sans text-${color}`}
                  key={label}
                >
                  {!!label && !!count && (
                    <Fragment>
                      <span className="inline-flex max-w-4/5 items-center gap-10">
                        <Icon className="shrink-0" name={icon} width="1em" />
                        <span className="truncate">{label}</span>
                      </span>
                      {count}
                    </Fragment>
                  )}
                </li>
              ) : null,
            )}
          </ul>
        )}
        {!!seeMoreLabel && <p className="my-15 text-center text-12 underline">{seeMoreLabel}</p>}
      </TeaserCard>
    </TagName>
  );
};
